import React, { useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { init } from '@telegram-apps/sdk-react';

import HomePage from './pages/HomePage';
import OperationsPage from './pages/OperationsPage';
import PurchaseKeyPage from './pages/PurchaseKeyPage';
import SubscriptionDetailsPage from './pages/SubscriptionDetailsPage';
import AdminPage from './pages/AdminPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminPricingPage from './pages/AdminPricingPage';
import AdminReferralPage from './pages/AdminReferralPage';
import ReferralPage from './pages/ReferralPage';
import VPNInstructionPage from './pages/VPNInstructionPage';
import TelegramLoader from './components/TelegramLoader';
import LoadingOverlay from './components/LoadingOverlay';
import useTelegramWebApp from './hooks/useTelegramWebApp';
import { AppProvider, useApp } from './context/AppContext';
import { getThemeConfig } from './constants/themes';

// Внутренний компонент App с доступом к контексту
const AppContent = () => {
  const { 
    currentTheme, 
    language,
    hasActiveLoading, 
    isLoading, 
    error, 
    isTelegramAvailable,
    telegramTheme,
    loadServers,
    servers
  } = useApp();
  
  const isRTL = language === 'fa';
  
  // Используем хук для инициализации Telegram WebApp
  useTelegramWebApp();

  // Загружаем серверы при старте приложения
  useEffect(() => {
    const initializeServers = async () => {
      // Загружаем серверы только если они еще не загружены
      if (!servers.servers || servers.servers.length === 0) {
        try {
          await loadServers();
          console.log('Серверы загружены при инициализации приложения');
        } catch (error) {
          console.error('Ошибка загрузки серверов при инициализации:', error);
        }
      }
    };

    initializeServers();
  }, [loadServers]); // Убираем servers из зависимостей чтобы избежать бесконечного цикла

  // Мемоизируем конфигурацию темы
  const themeConfig = useMemo(() => 
    getThemeConfig(currentTheme), 
    [currentTheme]
  );

  // Мемоизируем стили контейнера
  const containerStyles = useMemo(() => ({
    minHeight: '100vh',
    padding: 'var(--tg-content-safe-area-inset-top, 0) var(--tg-content-safe-area-inset-right, 0) var(--tg-content-safe-area-inset-bottom, 0) var(--tg-content-safe-area-inset-left, 0)',
  }), []);

  // Синхронизируем CSS класс с темой для анимации
  useEffect(() => {
    document.documentElement.className = `theme-${currentTheme}`;
  }, [currentTheme]);

  // Синхронизируем направление текста для RTL языков (Persian/Farsi)
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [isRTL]);

  // Показываем TelegramLoader только при первоначальной инициализации или ошибке
  if (isLoading || error) {
    return (
      <ConfigProvider theme={themeConfig} direction={isRTL ? 'rtl' : 'ltr'}>
        <TelegramLoader 
          isLoading={isLoading} 
          error={error}
        />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={themeConfig} direction={isRTL ? 'rtl' : 'ltr'}>
      <div className="app-container" style={containerStyles}>
        <Router>
          <Routes>
            <Route 
              path="/" 
              element={
                <HomePage 
                  telegramTheme={telegramTheme}
                  isTelegramAvailable={isTelegramAvailable}
                />
              } 
            />
            <Route path="/operations" element={<OperationsPage />} />
            <Route path="/purchase-key" element={<PurchaseKeyPage />} />
            <Route path="/subscription/:id" element={<SubscriptionDetailsPage />} />
            <Route 
              path="/vpn-instruction" 
              element={
                <VPNInstructionPage 
                  isTelegramAvailable={isTelegramAvailable}
                />
              } 
            />
            
            {/* Админ-панель */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/pricing" element={<AdminPricingPage />} />
            <Route path="/admin/referral" element={<AdminReferralPage />} />
            <Route path="/referral" element={<ReferralPage />} />
          </Routes>
        </Router>

        {/* Универсальный overlay загрузки для любых операций */}
        <LoadingOverlay 
          isVisible={hasActiveLoading && !isLoading} 
          zIndex={9999}
        />
      </div>
    </ConfigProvider>
  );
};

// Главный компонент App с провайдером контекста
function App() {
  useEffect(() => {
    init();
  }, []);

  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
